import {
  Directive,
  ElementRef,
  OnDestroy,
  afterNextRender,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { CompanionChatService } from '../services/companion-chat.service';

/**
 * Keeps a scroll container pinned to the bottom while content grows,
 * unless the user scrolls up. Scrolling back to the bottom re-enables pinning.
 */
@Directive({
  selector: '[appStickToBottom]',
  exportAs: 'stickToBottom',
  host: {
    '(scroll)': 'onScroll()',
  },
})
export class StickToBottomDirective implements OnDestroy {
  private static readonly THRESHOLD_PX = 80;

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly companionChat = inject(CompanionChatService);

  private stickToBottom = true;
  private mutationObserver: MutationObserver | null = null;

  constructor() {
    effect(() => {
      this.companionChat.scrollRequest();
      untracked(() => this.scrollIfStuck());
    });

    afterNextRender(() => {
      this.mutationObserver = new MutationObserver(() => this.scrollIfStuck());
      this.mutationObserver.observe(this.elementRef.nativeElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      this.scrollIfStuck();
    });
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
  }

  /**
   * Forces stick-to-bottom and scrolls immediately (e.g. after the user sends).
   * @returns {void}
   */
  stick(): void {
    this.stickToBottom = true;
    this.scrollIfStuck();
  }

  /**
   * Updates stick state from the current scroll position.
   * @returns {void}
   */
  onScroll(): void {
    const element = this.elementRef.nativeElement;
    const distanceFromBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    this.stickToBottom =
      distanceFromBottom <= StickToBottomDirective.THRESHOLD_PX;
  }

  /**
   * Scrolls to the bottom when stick-to-bottom is active.
   * @returns {void}
   */
  private scrollIfStuck(): void {
    if (!this.stickToBottom) {
      return;
    }
    const element = this.elementRef.nativeElement;
    element.scrollTop = element.scrollHeight;
  }
}
